/**
 * Client HTTP de l'API SwissTech.
 *
 * Encapsule fetch pour centraliser l'URL de base, la sérialisation JSON et la
 * traduction des réponses d'erreur `application/problem+json` renvoyées par le
 * backend en exceptions exploitables par l'interface.
 *
 * La session voyage dans un cookie que le navigateur pose et renvoie seul : rien
 * n'est conservé ni lu par le code de la page, de sorte qu'une injection de
 * script ne saurait l'exfiltrer.
 */

/** Abonnés prévenus lorsque le serveur ne reconnaît plus la session. */
const abonnesSessionPerdue = new Set();

/**
 * S'abonne à la perte de session, pour ramener l'utilisateur à la connexion.
 *
 * @param {Function} abonne fonction appelée sur un refus d'authentification
 * @returns {Function} le désabonnement
 */
export function surSessionPerdue(abonne) {
  abonnesSessionPerdue.add(abonne);
  return () => abonnesSessionPerdue.delete(abonne);
}

/** URL de base de l'API. Vide en développement : le proxy Vite prend le relais. */
const BASE_URL = import.meta.env?.VITE_API_BASE_URL ?? '';

/** Erreur portant le statut HTTP et le détail renvoyé par le backend. */
export class ApiError extends Error {
  /**
   * @param {string} message message lisible par l'utilisateur
   * @param {object} details statut, titre et champs invalides éventuels
   */
  constructor(message, details = {}) {
    super(message);
    /** @type {boolean} vrai quand le serveur n'a pas pu être joint */
    this.reseau = details.reseau === true;
    this.name = 'ApiError';
    this.status = details.status ?? 0;
    this.title = details.title ?? '';
    /** Champs invalides, indexés par nom, pour un statut 400. */
    this.champs = details.champs ?? {};
    /** Type du problème (RFC 7807), qui distingue un refus d'un autre. */
    this.type = details.type ?? '';
    /** Formule qui lèverait la limite atteinte, quand le serveur en propose une. */
    this.formuleSuperieure = details.formuleSuperieure ?? null;
  }

  /** @returns {boolean} vrai si la formule souscrite n'autorise pas l'ajout demandé */
  get estLimiteFormule() {
    return this.status === 409 && this.type.endsWith('/limite-formule');
  }

  /** @returns {boolean} vrai si la ressource demandée n'existe pas */
  get estIntrouvable() {
    return this.status === 404;
  }

  /** @returns {boolean} vrai si une règle métier a été enfreinte */
  get estConflit() {
    return this.status === 409;
  }

  /** @returns {boolean} vrai si la requête a été rejetée par la validation */
  get estInvalide() {
    return this.status === 400;
  }

  /** @returns {boolean} vrai si la session manque ou n'est plus valable */
  get estNonAuthentifie() {
    return this.status === 401;
  }

  /** @returns {boolean} vrai si le rôle du compte n'autorise pas l'accès */
  get estInterdit() {
    return this.status === 403;
  }
}

/**
 * Compose l'adresse absolue d'une ressource servie par l'API.
 *
 * <p>Sert aux images, que le navigateur va chercher lui-même : elles ne passent
 * pas par `fetch` et ne bénéficient donc pas de l'URL de base appliquée ici.
 *
 * @param {string} chemin chemin renvoyé par l'API, commençant par `/api`
 * @returns {string} l'adresse à donner au navigateur
 */
export function adresseApi(chemin) {
  return `${BASE_URL}${chemin}`;
}

/** Construit une URL avec ses paramètres de requête, en ignorant les valeurs vides. */
function construitUrl(chemin, params) {
  const url = `${BASE_URL}/api${chemin}`;
  if (!params) return url;

  const query = new URLSearchParams();
  for (const [cle, valeur] of Object.entries(params)) {
    if (valeur !== undefined && valeur !== null && valeur !== '') {
      query.append(cle, String(valeur));
    }
  }
  const suffixe = query.toString();
  return suffixe ? `${url}?${suffixe}` : url;
}

/** Transforme une réponse en erreur exploitable, en lisant le corps problem+json. */
async function versErreur(reponse) {
  let corps = null;
  try {
    corps = await reponse.json();
  } catch {
    // Réponse sans corps JSON : on se rabat sur le statut.
  }

  const message = corps?.detail || corps?.title || `La requête a échoué (HTTP ${reponse.status}).`;

  return new ApiError(message, {
    status: reponse.status,
    title: corps?.title,
    champs: corps?.champs,
    type: corps?.type,
    formuleSuperieure: corps?.formuleSuperieure,
  });
}

/**
 * Exécute une requête vers l'API.
 *
 * @param {string} chemin chemin relatif sous `/api`
 * @param {object} [options] méthode, corps, paramètres de requête et signal d'annulation
 * @returns {Promise<any>} le corps de la réponse, ou `null` pour un 204
 * @throws {ApiError} si le serveur répond une erreur
 */
export async function requete(chemin, options = {}) {
  const { method = 'GET', body, params, signal, headers = {} } = options;

  // Un envoi de fichier porte son propre en-tête de délimitation, que le
  // navigateur compose : le fixer ici casserait l'enveloppe.
  const estFormulaire = typeof FormData !== 'undefined' && body instanceof FormData;

  let reponse;
  try {
    reponse = await fetch(construitUrl(chemin, params), {
      method,
      signal,
      // Le cookie de session doit accompagner la requête, y compris lorsque
      // l'interface vise une API servie par une autre origine.
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        ...(body !== undefined && !estFormulaire ? { 'Content-Type': 'application/json' } : {}),
        ...headers,
      },
      ...(body !== undefined ? { body: estFormulaire ? body : JSON.stringify(body) } : {}),
    });
  } catch (e) {
    // « Failed to fetch » ne dit rien a un artisan sur un chantier sans
    // couverture : la panne est nommee, et marquee comme telle pour que la
    // synchronisation sache qu'elle peut reessayer.
    if (e?.name === 'AbortError') throw e;
    throw new ApiError(
      'Pas de connexion au serveur. Tes modifications seront renvoyées dès que le réseau revient.',
      { status: 0, reseau: true },
    );
  }

  if (!reponse.ok) {
    const erreur = await versErreur(reponse);
    // Une session expirée peut survenir n'importe où : plutôt que d'obliger
    // chaque écran à s'en occuper, le client prévient une fois et l'interface
    // revient à l'écran de connexion.
    if (erreur.status === 401 && !options.ignoreSessionPerdue) {
      for (const abonne of abonnesSessionPerdue) abonne(erreur);
    }
    throw erreur;
  }

  if (reponse.status === 204 || reponse.headers.get('content-length') === '0') {
    return null;
  }

  const type = reponse.headers.get('content-type') || '';
  return type.includes('application/json') ? reponse.json() : reponse.text();
}

/** Raccourcis par verbe HTTP. */
/** Taille d'une page demandée quand on parcourt une collection entière. */
const PAGE_COMPLETE = 500;

/**
 * Nombre de pages qu'on accepte de parcourir avant de s'arrêter.
 *
 * <p>Une garde, non une limite de travail : à cinq cents par page, elle laisse
 * passer cinquante mille documents. Au-delà, mieux vaut une erreur franche
 * qu'une boucle qui ne finit pas.
 */
const PAGES_MAX = 100;

/**
 * Charge une collection paginée en entier.
 *
 * <p>Les écrans travaillent sur la collection complète : la comptabilité
 * additionne toutes les factures, la numérotation cherche le plus grand numéro
 * déjà posé. Une première page, si large soit-elle, ne suffit donc pas — elle
 * donnait un chiffre d'affaires calculé sur une partie des factures, et faisait
 * rééditer des numéros déjà utilisés.
 *
 * @param {string} chemin chemin de la collection
 * @param {object} [params] filtres, hors pagination
 * @returns {Promise<object[]>} tous les éléments, dans l'ordre du serveur
 */
export async function listeComplete(chemin, params) {
  const tout = [];
  for (let page = 0; page < PAGES_MAX; page += 1) {
    const reponse = await requete(chemin, {
      params: { ...params, page, size: PAGE_COMPLETE },
    });
    const lot = Array.isArray(reponse) ? reponse : (reponse?.content ?? []);
    tout.push(...lot);
    // Une réponse non paginée, ou la dernière page : il n'y a plus rien après.
    if (Array.isArray(reponse) || reponse?.last === true || lot.length < PAGE_COMPLETE) {
      return tout;
    }
  }
  throw new Error(
    `La collection ${chemin} dépasse ${PAGES_MAX * PAGE_COMPLETE} éléments : chargement interrompu.`,
  );
}

export const api = {
  /**
   * @param {string} chemin chemin relatif
   * @param {object} [params] paramètres de requête
   * @param {object} [options] options supplémentaires
   */
  get: (chemin, params, options) => requete(chemin, { ...options, params }),
  /**
   * @param {string} chemin chemin relatif
   * @param {any} [body] corps à envoyer
   * @param {object} [options] options supplémentaires
   */
  post: (chemin, body, options) => requete(chemin, { ...options, method: 'POST', body }),
  /**
   * @param {string} chemin chemin relatif
   * @param {any} body corps à envoyer
   * @param {object} [options] options supplémentaires
   */
  put: (chemin, body, options) => requete(chemin, { ...options, method: 'PUT', body }),
  /**
   * @param {string} chemin chemin relatif
   * @param {object} [options] options supplémentaires
   */
  delete: (chemin, options) => requete(chemin, { ...options, method: 'DELETE' }),
};
